/**
 * Local-first store for the astro-journal and the accuracy loop.
 *
 * WHY THIS IS LOCAL. `Backend` (app/src/lib/backend/types.ts) has no journal
 * methods yet — the callables exist server-side (`journalAdd`, `accuracyRate`,
 * `accuracyStats`) but nothing in the app is wired to them. Journal bodies are
 * also the most personal text in the product and are explicitly offline-first,
 * so IndexedDB is the correct home for them regardless. Everything below is
 * shaped so a later sync is a drop-in: the record types are the shared
 * contracts verbatim, ids match the server's natural ids, and the aggregate is
 * recomputed from the ratings rather than stored as truth.
 *
 * Honest framing (house rule): a hit rate here is a record of how a reader
 * FELT a lens fitted their own life. Nothing in this file may be presented as
 * evidence that astrology predicts events.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyVerdict,
  emptyAccuracyStats,
  emptyAccuracyTally,
  type AccuracyRating,
  type AccuracyStats,
  type AccuracyTally,
  type AccuracyVerdict,
  type AstroSystem,
  type HoroscopePeriod,
  type JournalEntry,
  type JournalEntryKind,
  type LifeArea,
  type LifeEventType,
  type Mood,
} from '@astrally/shared';
import { localdb } from '../../lib/localdb';

// ---------------------------------------------------------------------------
// Keys and ids
// ---------------------------------------------------------------------------

const entriesKey = (uid: string) => `journal:${uid}`;
const ratingsKey = (uid: string) => `accuracyRatings:${uid}`;

function newId(prefix: string): string {
  // Android System WebView on older devices has no crypto.randomUUID.
  const rnd =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}-${rnd}`;
}

/**
 * The natural id of a rating: one verdict per signal per reading. Re-rating
 * overwrites rather than appending, which is what makes the aggregate safe to
 * recompute by replay.
 */
export function ratingIdFor(readingKey: string, signalId: string): string {
  return `${readingKey}_${signalId}`;
}

/**
 * LOCAL calendar day, not UTC. A 1 a.m. entry in Kolkata belongs to that day
 * for the reader, and the streak is counted in the days they actually lived.
 */
export function localDayKey(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * A `<input type="date">` value turned into an instant.
 *
 * Local NOON, deliberately. Midnight lands on the previous day for anyone west
 * of UTC once the ISO string is read back, which silently shifts a backdated
 * life event by a day — and dates are the whole point of the dasha overlay.
 */
export function dateInputToISO(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return new Date().toISOString();
  return new Date(y, m - 1, d, 12, 0, 0, 0).toISOString();
}

/** The inverse, for populating the editor. */
export function isoToDateInput(iso: string): string {
  return localDayKey(iso);
}

// ---------------------------------------------------------------------------
// Drafts
// ---------------------------------------------------------------------------

/** What the editor hands back. Ids and timestamps are the store's business. */
export interface JournalDraft {
  atISO: string;
  kind: JournalEntryKind;
  title: string;
  body?: string;
  tags: string[];
  mood?: Mood;
  lifeEventType?: LifeEventType;
  linkedReadingKey?: string;
  linkedSystem?: AstroSystem;
  linkedSignalId?: string;
}

export function emptyDraft(kind: JournalEntryKind = 'note'): JournalDraft {
  return { atISO: new Date().toISOString(), kind, title: '', tags: [] };
}

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

/** Newest first. `atISO` is what the entry is ABOUT, so backdating reorders. */
function sortEntries(entries: JournalEntry[]): JournalEntry[] {
  return [...entries].sort(
    (a, b) =>
      Date.parse(b.atISO) - Date.parse(a.atISO) ||
      Date.parse(b.createdAtISO) - Date.parse(a.createdAtISO),
  );
}

export async function loadEntries(uid: string): Promise<JournalEntry[]> {
  const raw = await localdb.get<JournalEntry[]>('kv', entriesKey(uid));
  return sortEntries(Array.isArray(raw) ? raw : []);
}

export async function loadRatings(uid: string): Promise<AccuracyRating[]> {
  const raw = await localdb.get<AccuracyRating[]>('kv', ratingsKey(uid));
  return Array.isArray(raw) ? raw : [];
}

async function writeEntries(uid: string, entries: JournalEntry[]): Promise<JournalEntry[]> {
  const sorted = sortEntries(entries);
  await localdb.set('kv', entriesKey(uid), sorted);
  return sorted;
}

async function writeRatings(uid: string, ratings: AccuracyRating[]): Promise<AccuracyRating[]> {
  await localdb.set('kv', ratingsKey(uid), ratings);
  return ratings;
}

// ---------------------------------------------------------------------------
// Mutations — pure over the array, so the hook can keep state and disk in step
// ---------------------------------------------------------------------------

export function buildEntry(uid: string, draft: JournalDraft): JournalEntry {
  const now = new Date().toISOString();
  return {
    id: newId('je'),
    uid,
    atISO: draft.atISO,
    kind: draft.kind,
    title: draft.title.trim(),
    body: draft.body?.trim() || undefined,
    tags: normaliseTags(draft.tags),
    mood: draft.mood,
    lifeEventType: draft.kind === 'life-event' ? draft.lifeEventType : undefined,
    linkedReadingKey: draft.linkedReadingKey,
    linkedSystem: draft.linkedSystem,
    linkedSignalId: draft.linkedSignalId,
    createdAtISO: now,
    updatedAtISO: now,
  };
}

/** Lowercased, trimmed, de-duplicated, order preserved. Never AI-populated. */
export function normaliseTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const tag = raw.trim().toLowerCase().replace(/^#/, '');
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Aggregate — recomputed from the ratings, never trusted from storage
// ---------------------------------------------------------------------------

/**
 * Consecutive local days with at least one rating, ending today or yesterday.
 *
 * Yesterday still counts: a streak that dies at midnight before the user has
 * had a chance to open the app punishes them for sleeping.
 */
export function ratingStreakDays(ratings: AccuracyRating[], now = new Date()): number {
  if (ratings.length === 0) return 0;
  const days = new Set(ratings.map((r) => localDayKey(r.ratedAtISO)).filter(Boolean));
  const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (!days.has(localDayKey(cursor))) {
    cursor.setDate(cursor.getDate() - 1);
    if (!days.has(localDayKey(cursor))) return 0;
  }
  let streak = 0;
  while (days.has(localDayKey(cursor))) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

export function computeStats(uid: string, ratings: AccuracyRating[], now = new Date()): AccuracyStats {
  const stats = emptyAccuracyStats(uid, now.toISOString());
  for (const r of ratings) {
    if (stats.bySystem[r.system]) stats.bySystem[r.system] = applyVerdict(stats.bySystem[r.system], r.verdict);
    if (stats.byArea[r.area]) stats.byArea[r.area] = applyVerdict(stats.byArea[r.area], r.verdict);
    if (stats.byPeriod[r.period]) stats.byPeriod[r.period] = applyVerdict(stats.byPeriod[r.period], r.verdict);
    stats.totalRated += 1;
  }
  stats.streakDays = ratingStreakDays(ratings, now);
  const last = ratings.reduce<string | undefined>(
    (acc, r) => (!acc || r.ratedAtISO > acc ? r.ratedAtISO : acc),
    undefined,
  );
  stats.lastRatedDayKey = last ? localDayKey(last) : undefined;
  return stats;
}

/** Every rating lands in exactly one system bucket, so this is the true total. */
export function overallTally(stats: AccuracyStats): AccuracyTally {
  const sum = emptyAccuracyTally();
  for (const tally of Object.values(stats.bySystem)) {
    sum.rated += tally.rated;
    sum.hits += tally.hits;
    sum.partials += tally.partials;
    sum.misses += tally.misses;
  }
  sum.hitRate = decisiveOf(sum) === 0 ? 0 : creditOf(sum) / decisiveOf(sum);
  return sum;
}

/** Ratings that count toward a hit rate. 'unclear' is excluded on both sides. */
export function decisiveOf(t: AccuracyTally): number {
  return t.hits + t.partials + t.misses;
}

/** Hits, with a partial worth half a hit. */
export function creditOf(t: AccuracyTally): number {
  return t.hits + t.partials * 0.5;
}

// ---------------------------------------------------------------------------
// Progress — the numbers that make the profile visibly grow
// ---------------------------------------------------------------------------

export interface JournalProgress {
  entries: number;
  rated: number;
  /** Distinct local days the user actually showed up: wrote or rated. */
  daysTracked: number;
  streakDays: number;
  lifeEvents: number;
}

export function computeProgress(
  entries: JournalEntry[],
  ratings: AccuracyRating[],
  now = new Date(),
): JournalProgress {
  const days = new Set<string>();
  // createdAtISO, not atISO: backdating twenty life events in one sitting is
  // one day of tracking, not twenty.
  for (const e of entries) days.add(localDayKey(e.createdAtISO));
  for (const r of ratings) days.add(localDayKey(r.ratedAtISO));
  days.delete('');
  return {
    entries: entries.length,
    rated: ratings.length,
    daysTracked: days.size,
    streakDays: ratingStreakDays(ratings, now),
    lifeEvents: entries.filter((e) => e.kind === 'life-event').length,
  };
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export interface RateSignalInput {
  /** `${system}-${period}-${periodKey}` — see functions/src/horoscope/cache.ts. */
  readingKey: string;
  signalId: string;
  verdict: AccuracyVerdict;
  note?: string;
  /**
   * Denormalised from the rated SIGNAL, never from a user's claim about it.
   * Firestore cannot join and `AccuracyStats` is uncomputable without them.
   */
  system: AstroSystem;
  period: HoroscopePeriod;
  area: LifeArea;
  /** Signal label, so the auto-written journal entry reads like something. */
  signalLabel?: string;
}

export interface JournalStore {
  ready: boolean;
  entries: JournalEntry[];
  ratings: AccuracyRating[];
  stats: AccuracyStats;
  progress: JournalProgress;
  addEntry(draft: JournalDraft): Promise<JournalEntry | null>;
  updateEntry(id: string, draft: JournalDraft): Promise<void>;
  deleteEntry(id: string): Promise<void>;
  /** Upserts the verdict AND keeps the timeline whole with a linked entry. */
  rateSignal(input: RateSignalInput): Promise<void>;
  reload(): Promise<void>;
}

export function useJournal(uid: string | null): JournalStore {
  const [ready, setReady] = useState(false);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [ratings, setRatings] = useState<AccuracyRating[]>([]);

  const reload = useCallback(async () => {
    if (!uid) {
      setEntries([]);
      setRatings([]);
      setReady(true);
      return;
    }
    const [e, r] = await Promise.all([loadEntries(uid), loadRatings(uid)]);
    setEntries(e);
    setRatings(r);
    setReady(true);
  }, [uid]);

  useEffect(() => {
    setReady(false);
    void reload();
  }, [reload]);

  const addEntry = useCallback(
    async (draft: JournalDraft): Promise<JournalEntry | null> => {
      if (!uid) return null;
      const entry = buildEntry(uid, draft);
      setEntries(await writeEntries(uid, [entry, ...entries]));
      return entry;
    },
    [entries, uid],
  );

  const updateEntry = useCallback(
    async (id: string, draft: JournalDraft) => {
      if (!uid) return;
      const now = new Date().toISOString();
      const next = entries.map((e) =>
        e.id === id
          ? {
              ...e,
              atISO: draft.atISO,
              kind: draft.kind,
              title: draft.title.trim(),
              body: draft.body?.trim() || undefined,
              tags: normaliseTags(draft.tags),
              mood: draft.mood,
              lifeEventType: draft.kind === 'life-event' ? draft.lifeEventType : undefined,
              updatedAtISO: now,
            }
          : e,
      );
      setEntries(await writeEntries(uid, next));
    },
    [entries, uid],
  );

  const deleteEntry = useCallback(
    async (id: string) => {
      if (!uid) return;
      setEntries(await writeEntries(uid, entries.filter((e) => e.id !== id)));
    },
    [entries, uid],
  );

  const rateSignal = useCallback(
    async (input: RateSignalInput) => {
      if (!uid) return;
      const id = ratingIdFor(input.readingKey, input.signalId);
      const rating: AccuracyRating = {
        id,
        uid,
        readingKey: input.readingKey,
        signalId: input.signalId,
        verdict: input.verdict,
        note: input.note?.trim() || undefined,
        system: input.system,
        period: input.period,
        area: input.area,
        ratedAtISO: new Date().toISOString(),
      };
      const previous = ratings.find((r) => r.id === id);
      const nextRatings = previous
        ? ratings.map((r) => (r.id === id ? rating : r))
        : [...ratings, rating];
      setRatings(await writeRatings(uid, nextRatings));

      // Keep the timeline whole: one entry per rated signal, updated in place
      // when the verdict changes, so re-rating never spams the journal.
      const label = input.signalLabel?.trim() || input.signalId;
      const existing = entries.find(
        (e) =>
          e.kind === 'prediction-rated' &&
          e.linkedReadingKey === input.readingKey &&
          e.linkedSignalId === input.signalId,
      );
      const title = `${VERDICT_VERB[input.verdict]} — ${label}`;
      if (existing) {
        setEntries(
          await writeEntries(
            uid,
            entries.map((e) =>
              e.id === existing.id
                ? { ...e, title, body: rating.note, updatedAtISO: rating.ratedAtISO }
                : e,
            ),
          ),
        );
      } else {
        setEntries(
          await writeEntries(uid, [
            buildEntry(uid, {
              atISO: rating.ratedAtISO,
              kind: 'prediction-rated',
              title,
              body: rating.note,
              tags: [input.area, input.system],
              linkedReadingKey: input.readingKey,
              linkedSystem: input.system,
              linkedSignalId: input.signalId,
            }),
            ...entries,
          ]),
        );
      }
    },
    [entries, ratings, uid],
  );

  const stats = useMemo(() => computeStats(uid ?? '', ratings), [ratings, uid]);
  const progress = useMemo(() => computeProgress(entries, ratings), [entries, ratings]);

  return {
    ready,
    entries,
    ratings,
    stats,
    progress,
    addEntry,
    updateEntry,
    deleteEntry,
    rateSignal,
    reload,
  };
}

/** Reads as a sentence in the auto-written journal entry. */
export const VERDICT_VERB: Record<AccuracyVerdict, string> = {
  hit: 'Landed',
  partial: 'Partly landed',
  miss: 'Missed',
  unclear: "Couldn't tell",
};

// ---------------------------------------------------------------------------
// For other features
// ---------------------------------------------------------------------------

/**
 * Save a reading into the journal from anywhere (e.g. the horoscope screen).
 * Writes straight to disk; a mounted `useJournal` picks it up on `reload()`.
 */
export async function saveReadingToJournal(
  uid: string,
  args: {
    headline: string;
    readingKey: string;
    system: AstroSystem;
    note?: string;
    atISO?: string;
  },
): Promise<JournalEntry> {
  const existing = await loadEntries(uid);
  const already = existing.find(
    (e) => e.kind === 'reading-saved' && e.linkedReadingKey === args.readingKey,
  );
  if (already) return already;
  const entry = buildEntry(uid, {
    atISO: args.atISO ?? new Date().toISOString(),
    kind: 'reading-saved',
    title: args.headline,
    body: args.note,
    tags: [args.system],
    linkedReadingKey: args.readingKey,
    linkedSystem: args.system,
  });
  await writeEntries(uid, [entry, ...existing]);
  return entry;
}
